import React, { useState, useEffect } from 'react';
import { useBioData } from '../../context/BioDataContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { gsPayTables } from '../../data/gsPayTables';
import { localityAdjustments } from '../../data/localityAdjustments';
import './DeferredFields.css';

const DeferredFields = ({ scenario, onScenarioChange }) => {
  const { bioData, handleBioChange } = useBioData();
  const [localData, setLocalData] = useState({
    finalGrade: bioData.currentGrade || 14,
    finalStep: bioData.currentStep || 5,
    lastDayOfService: scenario.lastDayOfService || '',
    separationDate: scenario.departureYear ? dayjs(`${scenario.departureYear}-12-31`) : null,
    high3SalaryAtSeparation: '',
    exactYearsOfService: '',
    exactMonthsOfService: '',
    exactDaysOfService: '',
    localityAtSeparation: bioData.locality || 'DC',
    userEditedHigh3: false // Track if user has manually edited the high-3
  });

  // Handle separation date changes
  const handleSeparationDateChange = (newDate) => {
    if (!newDate) return;
    
    const selectedYear = newDate.year();
    const selectedMonth = newDate.month();
    const selectedDay = newDate.date();
    
    // Format the date for storage
    const formattedDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    
    // Update the local state
    setLocalData(prev => ({
      ...prev,
      separationDate: newDate,
      lastDayOfService: formattedDate
    }));
    
    // Update the scenario
    onScenarioChange(scenario.id, 'lastDayOfService', formattedDate);
    onScenarioChange(scenario.id, 'departureYear', selectedYear);
    
    // Calculate precise service time if EOD is available
    if (bioData.entryOnDuty) {
      const eodDate = new Date(bioData.entryOnDuty);
      if (!isNaN(eodDate.getFullYear())) {
        const serviceComputation = calculatePreciseServiceTime(eodDate, new Date(formattedDate));
        
        // Update the local form fields with the calculated values
        setLocalData(prev => ({
          ...prev,
          exactYearsOfService: serviceComputation.years.toString(),
          exactMonthsOfService: serviceComputation.months.toString(),
          exactDaysOfService: serviceComputation.days.toString()
        }));
        
        // Store in scenario context both the years (for basic calculations) and the precise breakdown
        onScenarioChange(scenario.id, 'yearsOfServiceAtDeparture', serviceComputation.years);
        onScenarioChange(scenario.id, 'preciseServiceComputation', serviceComputation);
        
        // Log the computation for debugging
        console.log('Service computation:', serviceComputation);
      }
    }
  };
  
  // Initialize the separation date when the component mounts
  useEffect(() => {
    if (scenario.lastDayOfService) {
      setLocalData(prev => ({
        ...prev,
        separationDate: dayjs(scenario.lastDayOfService),
        lastDayOfService: scenario.lastDayOfService
      }));
    } else if (scenario.departureYear) {
      // If we only have the departure year, set to December 31st of that year
      const date = dayjs(`${scenario.departureYear}-12-31`);
      setLocalData(prev => ({
        ...prev,
        separationDate: date,
        lastDayOfService: date.format('YYYY-MM-DD')
      }));
      onScenarioChange(scenario.id, 'lastDayOfService', date.format('YYYY-MM-DD'));
    } else {
      // If no date is set, default to December 31, 2025
      const defaultDate = dayjs('2025-12-31');
      setLocalData(prev => ({
        ...prev,
        separationDate: defaultDate,
        lastDayOfService: '2025-12-31'
      }));
      onScenarioChange(scenario.id, 'lastDayOfService', '2025-12-31');
      onScenarioChange(scenario.id, 'departureYear', 2025);
    }
  }, []);

  // Calculate precise service time including years, months, and days
  const calculatePreciseServiceTime = (startDate, endDate) => {
    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();
    let days = endDate.getDate() - startDate.getDate();
    
    // Adjust for negative days (borrowing from months)
    if (days < 0) {
      // Get days in the previous month of the end date
      const daysInPrevMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0).getDate();
      days += daysInPrevMonth;
      months -= 1;
    }
    
    // Adjust for negative months (borrowing from years)
    if (months < 0) {
      months += 12;
      years -= 1;
    }
    
    // Edge case: if years is negative, reset all to zero
    if (years < 0) {
      years = 0;
      months = 0;
      days = 0;
    }
    
    return {
      years,
      months,
      days,
      // Total as decimal for precise calculations
      totalYears: years + (months / 12) + (days / 365.25),
      // String representation for display
      display: `${years} years, ${months} months, ${days} days`
    };
  };

  const handleLocalChange = (field, value) => {
    setLocalData(prev => ({
      ...prev,
      [field]: value
    }));

    // Update bio data for fields that should be global
    if (['finalGrade', 'finalStep', 'localityAtSeparation'].includes(field)) {
      const bioFieldMap = {
        finalGrade: 'currentGrade',
        finalStep: 'currentStep',
        localityAtSeparation: 'locality'
      };
      if (bioFieldMap[field]) {
        handleBioChange(bioFieldMap[field], value);
      }
    }
  };

  // Calculate estimated high-3 based on grade/step
  const calculateEstimatedHigh3 = () => {
    const { finalGrade, finalStep, localityAtSeparation } = localData;
    
    // Get base pay for the selected grade/step
    if (!gsPayTables || !gsPayTables.base) {
      console.error('GSPayTables not available');
      return 0;
    }
    
    const baseAnnualSalary = gsPayTables.base[finalGrade]?.[finalStep - 1] || 0;
    if (!baseAnnualSalary) {
      console.error('Could not find base salary for Grade', finalGrade, 'Step', finalStep);
      return 0;
    }
    
    // Apply locality adjustment
    const localityRate = localityAdjustments[localityAtSeparation] || 1;
    const adjustedSalary = Math.round(baseAnnualSalary * localityRate);
    
    return adjustedSalary;
  };
  
  // Listen for changes to grade/step/locality and recalculate high-3
  useEffect(() => {
    // Only recalculate if the user hasn't manually edited the high-3
    if (!localData.userEditedHigh3) {
      const estimated = calculateEstimatedHigh3();
      
      setLocalData(prev => ({
        ...prev,
        high3SalaryAtSeparation: estimated.toString()
      }));
      
      // Update the scenario with the estimated high-3 salary for deferred pension calculation
      onScenarioChange(scenario.id, 'high3SalaryAtSeparation', estimated);
    }
  }, [localData.finalGrade, localData.finalStep, localData.localityAtSeparation, localData.userEditedHigh3, scenario.id, onScenarioChange]);

  return (
    <div className="deferred-fields">
      <h3>Deferred Retirement Details</h3>
      <p className="section-intro">
        Enter information about your separation from federal service. This works for both
        former employees and those planning to leave in the future.
      </p>
      <div className="info-box">
        <h5>Important Information About Deferred Retirement</h5>
        <ul>
          <li><strong>Separation vs. Benefit Start:</strong> Your separation date is when you leave service, while your benefit start date is when you'll begin receiving pension payments (usually at MRA or later).</li>
          <li><strong>High-3 Salary:</strong> Your pension is based on your high-3 salary at the time you separate, calculated from your salary history.</li>
          <li><strong>Service Computation:</strong> Only service up to your separation date counts toward your pension.</li>
          <li><strong>Sick Leave:</strong> Unlike immediate retirement, unused sick leave is not credited in deferred retirement calculations.</li>
        </ul>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="separationDate">Separation Date:</label>
          <DatePicker
            value={localData.separationDate}
            onChange={handleSeparationDateChange}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <span className="help-text">
            Your official separation date (or planned date) from federal service
          </span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="finalGrade">Final Grade:</label>
          <input
            type="number"
            id="finalGrade"
            value={localData.finalGrade}
            onChange={(e) => handleLocalChange('finalGrade', parseInt(e.target.value))}
            className="form-input"
            min="1"
            max="15"
          />
          <span className="help-text">
            Your GS grade at separation (or projected grade at separation)
          </span>
        </div>
        
        <div className="form-group">
          <label htmlFor="finalStep">Final Step:</label>
          <input
            type="number"
            id="finalStep"
            value={localData.finalStep}
            onChange={(e) => handleLocalChange('finalStep', parseInt(e.target.value))}
            className="form-input"
            min="1"
            max="10"
          />
          <span className="help-text">
            Your step level at separation (or projected step at separation)
          </span>
        </div>
        
        <div className="form-group">
          <label htmlFor="localityAtSeparation">Locality Pay Area:</label>
          <select
            id="localityAtSeparation"
            value={localData.localityAtSeparation}
            onChange={(e) => handleLocalChange('localityAtSeparation', e.target.value)}
            className="form-input"
          >
            <option value="DC">Washington-Baltimore-Arlington, DC-MD-VA-WV-PA</option>
            <option value="SF">San Francisco-Oakland-Berkeley, CA</option>
            <option value="NY">New York-Newark, NY-NJ-CT-PA</option>
            <option value="LA">Los Angeles-Long Beach, CA</option>
            <option value="CHI">Chicago-Naperville, IL-IN-WI</option>
            <option value="REST">Rest of U.S.</option>
          </select>
          <span className="help-text">
            Your locality pay area at separation
          </span>
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="high3SalaryAtSeparation">High-3 Salary at Separation:</label>
          <input
            type="number"
            id="high3SalaryAtSeparation"
            value={localData.high3SalaryAtSeparation}
            onChange={(e) => {
              const value = e.target.value;
              handleLocalChange('high3SalaryAtSeparation', value);
              handleLocalChange('userEditedHigh3', true);
              onScenarioChange(scenario.id, 'high3SalaryAtSeparation', parseFloat(value) || 0);
            }}
            className="form-input"
            min="0"
            step="100"
          />
          <span className="help-text">
            Your highest 3-year average salary at separation (calculated from grade/step, or enter manually)
          </span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Precise Service Computation:</label>
          <div className="service-computation-inputs">
            <div className="field-group small">
              <label htmlFor="exactYearsOfService">Years:</label>
              <input
                type="number"
                id="exactYearsOfService"
                value={localData.exactYearsOfService}
                onChange={(e) => {
                  const value = e.target.value;
                  handleLocalChange('exactYearsOfService', value);
                  
                  // If user manually enters service time, update the scenario
                  if (localData.exactMonthsOfService && localData.exactDaysOfService) {
                    const years = parseInt(value) || 0;
                    const months = parseInt(localData.exactMonthsOfService) || 0;
                    const days = parseInt(localData.exactDaysOfService) || 0;
                    
                    // Calculate total years including fractional parts
                    const totalYears = years + (months / 12) + (days / 365.25);
                    
                    // Update the scenario with the manually entered service time
                    onScenarioChange(scenario.id, 'yearsOfServiceAtDeparture', years);
                    onScenarioChange(scenario.id, 'preciseServiceComputation', {
                      years,
                      months,
                      days,
                      totalYears,
                      display: `${years} years, ${months} months, ${days} days`
                    });
                  }
                }}
                className="form-input"
                min="0"
                max="50"
              />
            </div>
            <div className="field-group small">
              <label htmlFor="exactMonthsOfService">Months:</label>
              <input
                type="number"
                id="exactMonthsOfService"
                value={localData.exactMonthsOfService}
                onChange={(e) => {
                  const value = e.target.value;
                  handleLocalChange('exactMonthsOfService', value);
                  
                  // If user manually enters service time, update the scenario
                  if (localData.exactYearsOfService && localData.exactDaysOfService) {
                    const years = parseInt(localData.exactYearsOfService) || 0;
                    const months = parseInt(value) || 0;
                    const days = parseInt(localData.exactDaysOfService) || 0;
                    
                    // Calculate total years including fractional parts
                    const totalYears = years + (months / 12) + (days / 365.25);
                    
                    // Update the scenario with the manually entered service time
                    onScenarioChange(scenario.id, 'yearsOfServiceAtDeparture', years);
                    onScenarioChange(scenario.id, 'preciseServiceComputation', {
                      years,
                      months,
                      days,
                      totalYears,
                      display: `${years} years, ${months} months, ${days} days`
                    });
                  }
                }}
                className="form-input"
                min="0"
                max="11"
              />
            </div>
            <div className="field-group small">
              <label htmlFor="exactDaysOfService">Days:</label>
              <input
                type="number"
                id="exactDaysOfService"
                value={localData.exactDaysOfService}
                onChange={(e) => {
                  const value = e.target.value;
                  handleLocalChange('exactDaysOfService', value);
                  
                  // If user manually enters service time, update the scenario
                  if (localData.exactYearsOfService && localData.exactMonthsOfService) {
                    const years = parseInt(localData.exactYearsOfService) || 0;
                    const months = parseInt(localData.exactMonthsOfService) || 0;
                    const days = parseInt(value) || 0;
                    
                    // Calculate total years including fractional parts
                    const totalYears = years + (months / 12) + (days / 365.25);
                    
                    // Update the scenario with the manually entered service time
                    onScenarioChange(scenario.id, 'yearsOfServiceAtDeparture', years);
                    onScenarioChange(scenario.id, 'preciseServiceComputation', {
                      years,
                      months,
                      days,
                      totalYears,
                      display: `${years} years, ${months} months, ${days} days`
                    });
                  }
                }}
                className="form-input"
                min="0"
                max="30"
              />
            </div>
          </div>
          <span className="help-text">
            Optional: Enter exact service time from your SF-50 or retirement estimate. 
            Leave blank to use calculated estimate.
          </span>
        </div>
      </div>

      <div className="important-note">
        <strong>Important:</strong> For the most accurate calculation, refer to your last SF-50 
        (Notification of Personnel Action) or any retirement estimates you received before leaving.
      </div>
    </div>
  );
};

export default DeferredFields;
